import { NavigationConfig } from '../types';
import { isRouteActive } from './route-matcher';

/**
 * Converts a pathname segment to a human-readable page name.
 * Example: "data-studio" -> "Data Studio"
 */
function formatPageNameFromPath(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Checks if a navigation item matches the given pathname
 */
function itemMatchesPathname(
  item: {
    href: string;
    activeMatcher?: string | ((path: string) => boolean);
    isExternal?: boolean;
    badgeText?: string;
  },
  pathname: string
): boolean {
  // Check activeMatcher first if provided (most specific)
  if (item.activeMatcher) {
    if (typeof item.activeMatcher === 'function') {
      if (item.activeMatcher(pathname)) {
        return true;
      }
    } else if (typeof item.activeMatcher === 'string') {
      const regex = new RegExp(item.activeMatcher);
      if (regex.test(pathname)) {
        return true;
      }
    }
  }

  // For external URLs, check if pathname matches the path portion
  if (item.isExternal && item.href) {
    try {
      const url = new URL(item.href);
      const externalPath = url.pathname;
      // Only match if pathname exactly equals external path (not includes, to avoid false matches)
      if (pathname === externalPath) {
        return true;
      }
    } catch {
      // If href is not a full URL, fall through to normal matching
    }
  }

  // Check normal route matching
  return isRouteActive(pathname, item.href, item.activeMatcher);
}

/**
 * Gets the badge text from the current pathname by matching it against the navigation config.
 * Returns the badgeText of the first matching navigation item, or null if no match is found.
 */
export function getPageBadge(
  pathname: string,
  config: NavigationConfig
): string | null {
  // Check all sections
  const sectionMatch = config.sections.find((section) => {
    const itemMatch = section.items.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (itemMatch) {
      return itemMatch.badgeText || null;
    }
    return false;
  });

  if (sectionMatch) {
    const itemMatch = sectionMatch.items.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (itemMatch?.badgeText) {
      return itemMatch.badgeText;
    }
  }

  // Check standalone links
  if (config.standaloneLinks) {
    const standaloneMatch = config.standaloneLinks.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (standaloneMatch?.badgeText) {
      return standaloneMatch.badgeText;
    }
  }

  // Check bottom links
  if (config.bottomLinks) {
    const bottomMatch = config.bottomLinks.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (bottomMatch?.badgeText) {
      return bottomMatch.badgeText;
    }
  }

  return null;
}

/**
 * Gets the page name from the current pathname by matching it against the navigation config.
 * Returns the label of the first matching navigation item, or a formatted version of the pathname if no match is found.
 */
export function getPageName(
  pathname: string,
  config: NavigationConfig
): string | null {
  // Check all sections
  const sectionMatch = config.sections.find((section) => {
    return section.items.some((item) => itemMatchesPathname(item, pathname));
  });

  if (sectionMatch) {
    const itemMatch = sectionMatch.items.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (itemMatch) {
      return itemMatch.label;
    }
  }

  // Check standalone links
  if (config.standaloneLinks) {
    const standaloneMatch = config.standaloneLinks.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (standaloneMatch) {
      return standaloneMatch.label;
    }
  }

  // Check bottom links
  if (config.bottomLinks) {
    const bottomMatch = config.bottomLinks.find((item) =>
      itemMatchesPathname(item, pathname)
    );
    if (bottomMatch) {
      return bottomMatch.label;
    }
  }

  // Fallback: try to extract page name from pathname
  // Match patterns like /org/:tenant/:page or /org/:tenant/:page/:subpage
  const orgMatch = pathname.match(/^\/org\/\d+\/([^/]+)/);
  if (orgMatch && orgMatch[1]) {
    const pageSegment = orgMatch[1];
    // Skip common segments that aren't page names
    if (!['homepage', 'home', 'dashboard'].includes(pageSegment)) {
      return formatPageNameFromPath(pageSegment);
    }
  }

  return null;
}
